import React from 'react';
import { formatClinicalText } from '../../utils/clinicalMapping';

/**
 * ManualReviewBadge - Displays review flag with theme-aware styling
 * 
 * @param {Object} meta - The evaluation meta object containing review info
 * @param {string} size - Badge size: 'sm', 'md', 'lg'
 * @param {boolean} showReasons - Whether to show review reasons
 */
export default function ManualReviewBadge({ meta, size = 'md', showReasons = false }) {
  if (!meta || !meta.needs_manual_review) {
    return null;
  }

  const { review_priority, review_reasons, confidence_score, confidence_level, reviewed } = meta;

  // Priority color mapping (theme-aware using CSS variables)
  const getPriorityStyles = (priority, review_status) => {
    if (review_status == true) {
      return {
        bg: 'bg-green-50',
        border: 'border-green-300',
        text: 'text-green-700',
        icon: '✅',
        label: 'Reviewed'
      };
    }
    switch (priority) {
      case 'critical':
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          text: 'text-red-700',
          icon: '🚨',
          label: 'Critical Review Required'
        };
      case 'high':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-300',
          text: 'text-orange-700',
          icon: '⚠️',
          label: 'High Priority Review'
        };
      case 'medium':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-300',
          text: 'text-yellow-700',
          icon: '⚡',
          label: 'Review Recommended'
        };
      case 'low':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-300',
          text: 'text-blue-700',
          icon: 'ℹ️',
          label: 'Minor Review Needed'
        };
      default:
        return {
          bg: 'bg-secondary-50',
          border: 'border-secondary-300',
          text: 'text-secondary-700',
          icon: '📋',
          label: 'Review Requested'
        };
    }
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const styles = getPriorityStyles(review_priority, reviewed);

  return (
    <div className="manual-review-badge">
      <span
        className={`inline-flex items-center gap-1.5 rounded-lg border-2 font-medium ${styles.bg} ${styles.border} ${styles.text} ${sizeClasses[size]}`}
        title={`Confidence: ${confidence_score || 'N/A'} (${confidence_level || 'unknown'})`}
      >
        <span>{styles.icon}</span>
        <span>{styles.label}</span>
      </span>

      {showReasons && review_reasons && review_reasons.length > 0 && (
        <div className={`mt-2 p-3 rounded-lg ${styles.bg} ${styles.border} border`}>
          <p className={`text-xs font-semibold ${styles.text} mb-1.5`}>Review Reasons:</p>
          <ul className="space-y-1">
            {review_reasons.map((reason, idx) => (
              <li key={idx} className={`text-xs ${styles.text} flex items-start gap-1.5`}>
                <span className="mt-0.5">•</span>
                <span>{formatClinicalText(reason)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
